import { useState, useEffect, useMemo, ChangeEvent, useRef } from 'react'

import { useAccount, useChainId } from 'wagmi'

import { Hash, TokenListItem } from 'types/entities'

import { useDebounce } from 'hooks/helpers'
import { useOnClickOutside } from 'hooks/listeners'
import { useCustomerTokens, useToken as useErc20 } from 'hooks/token'

import { useSetToken, useToken } from 'state/airdrop/hooks'

import { findArrayObjectField } from 'utils/helpers'
import { compareAddresses, isAddress } from 'utils/crypto'

import { errors } from 'constants/errors'
import { ETH_ADDRESS } from 'constants/addresses'
import { Icon, Loader, Slash, Tooltip } from 'components/ui'

import { TokenList } from '../TokenList'
import { Wrapper, Container, Label, IconWrap, SearchIcon, Search, ErrorMessage } from './Style'

type SearchBarProps = {
  label: string
  error?: boolean
  placeholder: string
}

// ToDo get tokensList req and tokenRequest from parent by props
// TODO: add K button for focus to SearchBar and add Cancel button when we focus
export function SearchBar({ label, placeholder }: SearchBarProps) {
  const searchBarRef = useRef<HTMLDivElement | null>(null)

  const token = useToken()
  const chainId = useChainId()
  const { address, isConnected } = useAccount()

  const setToken = useSetToken()
  const tokenRequest = useErc20()
  const { tokenList, addToken, ...fetchTokensReq } = useCustomerTokens()

  const [isOpen, setOpen] = useState(false)
  const [isError, setIsError] = useState(false)
  const [inputTokenValue, setInputTokenValue] = useState(token?.address || '')

  const isTokenExist = useMemo(() => {
    if (!tokenList || fetchTokensReq.isLoading) {
      return true
    }
    const selectedToken = tokenList.find((tokenFromList) => compareAddresses(tokenFromList.address, token?.address))
    return Boolean(selectedToken)
  }, [chainId, tokenList, fetchTokensReq.isLoading, token?.address])

  useEffect(() => {
    if (!isTokenExist) {
      setToken(null)
    }
  }, [isTokenExist])

  const _tokenList = useMemo(() => {
    if (!tokenList) {
      return []
    }

    const etherIndex = tokenList.findIndex((token) => token.address === ETH_ADDRESS)

    if (etherIndex === -1) {
      return []
    } else if (etherIndex === 0) {
      return tokenList.map((token) => ({ ...token, label: `${token.symbol}-${token.address}` }))
    }

    const sortedTokenList = [tokenList[etherIndex], ...tokenList.filter((token) => token.address !== ETH_ADDRESS)]

    return sortedTokenList.map((token) => ({ ...token, label: `${token?.symbol}-${token.address}` }))
  }, [tokenList])

  useOnClickOutside<HTMLDivElement>(searchBarRef, () => {
    onClickOutside()
  })

  useEffect(() => {
    if (!token?.address && inputTokenValue) {
      setInputTokenValue('')
    }
  }, [token?.address])

  const filteredTokenAddress = useDebounce(
    useMemo(() => (isAddress(inputTokenValue) ? (inputTokenValue as Hash) : undefined), [inputTokenValue]),
    300,
  )

  useEffect(() => {
    updateToken(filteredTokenAddress)
  }, [filteredTokenAddress, chainId])

  const filteredTokens = useMemo(
    () => findArrayObjectField<TokenListItem>(_tokenList, inputTokenValue),
    [_tokenList, inputTokenValue],
  )

  const error = useMemo(() => {
    if (inputTokenValue.length > 0 && !isAddress(inputTokenValue)) {
      setIsError(true)
      return errors.validation.INVALID_TOKEN
    }

    if (address && (fetchTokensReq.isError || tokenRequest.isError)) {
      setIsError(true)
      return fetchTokensReq.errorMessage || tokenRequest.errorMessage
    }
    return ''
  }, [
    address,
    inputTokenValue,
    tokenRequest.isError,
    fetchTokensReq.isError,
    tokenRequest.errorMessage,
    fetchTokensReq.errorMessage,
  ])

  const updateToken = async (address?: Hash) => {
    setIsError(false)

    if (!address?.trim()) {
      return setToken(null)
    }

    const existingToken = findExistToken(address)

    if (existingToken) {
      return setToken(existingToken)
    }

    const _token = await tokenRequest.call(address)
    if (_token) {
      setToken(_token)
    }

    if (_token && !isTokenExist) {
      addToken(_token)
    }
  }

  const findExistToken = (searchableAddress?: string) => {
    if (!searchableAddress) {
      return undefined
    }
    return _tokenList.find((elem) => compareAddresses(elem.address, searchableAddress))
  }

  const handleChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    setInputTokenValue(target.value)
  }

  const onSelect = (token: TokenListItem) => {
    setInputTokenValue(token.address)
  }

  const onClickOutside = () => {
    if (isOpen) {
      onShowListToggle()
    }
  }

  const onShowListToggle = () => {
    setOpen(!isOpen)
  }

  const onFocus = () => {
    if (filteredTokens?.length) {
      setOpen(true)
    }
  }

  const isLoading = useMemo(() => {
    return fetchTokensReq.isLoading || tokenRequest.isLoading
  }, [fetchTokensReq.isLoading, tokenRequest.isLoading])

  return (
    <Wrapper>
      <Label htmlFor={label}>
        <Slash />
        {label}
      </Label>

      <Container ref={searchBarRef}>
        <IconWrap>
          {isLoading ? (
            <Loader />
          ) : (
            <SearchIcon>
              <Icon name='search' size='fill' />
            </SearchIcon>
          )}
        </IconWrap>

        <Search
          id={label}
          type='search'
          error={isError}
          onFocus={onFocus}
          onChange={handleChange}
          value={inputTokenValue}
          placeholder={placeholder}
        />

        {!isConnected && (
          <Tooltip id={label} place='top' effect='solid'>
            {errors.validation.WALLET_REQUIRED}
          </Tooltip>
        )}

        {isError && !isLoading && <ErrorMessage>{error}</ErrorMessage>}

        {address && isOpen && (
          <TokenList
            loading={isLoading}
            onSelect={onSelect}
            onToggle={onShowListToggle}
            filteredTokens={filteredTokens}
          />
        )}
      </Container>
    </Wrapper>
  )
}
